import { apiClient } from '@/services/shared/apiClient';
import {
  Facility,
  FacilityBooking,
  FacilityReview,
  WeeklyHours,
  SeasonalHours,
} from '@/hooks/entities/admin/useFacilities';

interface MaintenanceSchedule {
  id: string;
  tenantId: string;
  facilityId: string;
  maintenanceType: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  scheduledDate: string;
  estimatedDuration: number;
  assignedTo?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  actualStartTime?: string;
  actualEndTime?: string;
  notes?: string;
  cost?: number;
  createdAt: string;
  updatedAt: string;
}

class FacilityService {
  // Facilities
  async getFacilities(tenantId?: string): Promise<Facility[]> {
    return apiClient.get<Facility[]>('/facilities', { tenantId });
  }

  async getFacility(facilityId: string): Promise<Facility> {
    return apiClient.get<Facility>(`/facilities/${facilityId}`);
  }

  async createFacility(
    tenantId: string,
    facilityData: Omit<
      Facility,
      | 'id'
      | 'tenantId'
      | 'totalBookings'
      | 'utilizationRate'
      | 'averageRating'
      | 'reviewCount'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<Facility> {
    return apiClient.post<Facility>('/facilities', { tenantId, ...facilityData });
  }

  // Helper method to get current operating hours based on season
  getCurrentOperatingHours(facility: Facility): WeeklyHours {
    if (!facility.useSeasonalHours || !facility.seasonalHours) {
      return facility.operatingHours;
    }

    const currentDate = new Date();
    const month = currentDate.getMonth(); // 0-11

    // Determine current season based on month
    let currentSeason: keyof SeasonalHours;
    if (month >= 2 && month <= 4) {
      currentSeason = 'spring'; // March-May
    } else if (month >= 5 && month <= 7) {
      currentSeason = 'summer'; // June-August
    } else if (month >= 8 && month <= 10) {
      currentSeason = 'fall'; // September-November
    } else {
      currentSeason = 'winter'; // December-February
    }

    return facility.seasonalHours[currentSeason];
  }

  // Helper method to check if facility is currently open
  isFacilityOpen(facility: Facility): boolean {
    const now = new Date();
    const daysOfWeek = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];
    const currentDay = daysOfWeek[now.getDay()] as keyof WeeklyHours;
    const currentTime = now.toTimeString().substring(0, 5); // HH:MM format

    const operatingHours = this.getCurrentOperatingHours(facility);
    const dayHours = operatingHours[currentDay];

    if (!dayHours) return false;

    return currentTime >= dayHours.open && currentTime <= dayHours.close;
  }

  // Helper method to get next opening time
  getNextOpeningTime(facility: Facility): { day: string; time: string } | null {
    const operatingHours = this.getCurrentOperatingHours(facility);
    const today = new Date();
    const daysOfWeek = [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ];

    for (let i = 0; i < 7; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() + i);
      const dayName = daysOfWeek[checkDate.getDay()] as keyof WeeklyHours;
      const dayHours = operatingHours[dayName];

      if (dayHours && dayHours.open) {
        const currentTime = today.toTimeString().substring(0, 5);

        // If it's today and still within operating hours, return today
        if (i === 0 && currentTime < dayHours.close) {
          return { day: dayName, time: dayHours.open };
        }
        // If it's a future day with operating hours, return that day
        else if (i > 0) {
          return { day: dayName, time: dayHours.open };
        }
      }
    }

    return null; // Facility never opens (shouldn't happen in practice)
  }

  async getAvailableFacilities(
    tenantId: string,
    startTime: string,
    endTime: string,
  ): Promise<Facility[]> {
    return apiClient.get<Facility[]>('/facilities/available', {
      tenantId,
      startTime,
      endTime,
    });
  }

  async updateFacility(facilityId: string, updates: Partial<Facility>): Promise<Facility> {
    return apiClient.put<Facility>(`/facilities/${facilityId}`, updates);
  }

  async deleteFacility(facilityId: string): Promise<void> {
    await apiClient.delete(`/facilities/${facilityId}`);
  }

  // Bookings
  async getBookings(
    tenantId: string,
    filters?: {
      facilityId?: string;
      userId?: string;
    },
  ): Promise<FacilityBooking[]> {
    return apiClient.get<FacilityBooking[]>('/facilities/bookings', {
      tenantId,
      ...filters,
    });
  }

  async createBooking(
    tenantId: string,
    bookingData: Omit<
      FacilityBooking,
      | 'id'
      | 'tenantId'
      | 'status'
      | 'confirmationSent'
      | 'reminderSent'
      | 'followUpSent'
      | 'checkedIn'
      | 'checkedOut'
      | 'damageReported'
      | 'damageCost'
      | 'refundAmount'
      | 'cancellationFee'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<FacilityBooking> {
    const fullBookingData = {
      tenantId,
      status: 'pending' as const,
      confirmationSent: false,
      reminderSent: false,
      followUpSent: false,
      checkedIn: false,
      checkedOut: false,
      damageReported: false,
      damageCost: 0,
      refundAmount: 0,
      cancellationFee: 0,
      ...bookingData,
    };
    return apiClient.post<FacilityBooking>('/facilities/bookings', fullBookingData);
  }

  async updateBooking(
    bookingId: string,
    updates: Partial<FacilityBooking>,
  ): Promise<FacilityBooking> {
    return apiClient.put<FacilityBooking>(`/facilities/bookings/${bookingId}`, updates);
  }

  async cancelBooking(bookingId: string, reason: string): Promise<FacilityBooking> {
    return apiClient.put<FacilityBooking>(`/facilities/bookings/${bookingId}/cancel`, {
      cancellationReason: reason,
    });
  }

  async checkAvailability(
    facilityId: string,
    startTime: string,
    endTime: string,
  ): Promise<boolean> {
    const result = await apiClient.get<{ available: boolean }>('/facilities/availability', {
      facilityId,
      startTime,
      endTime,
    });
    return result.available;
  }

  // Reviews
  async getFacilityReviews(facilityId: string): Promise<FacilityReview[]> {
    return apiClient.get<FacilityReview[]>(`/facilities/${facilityId}/reviews`);
  }

  async createReview(
    tenantId: string,
    reviewData: Omit<
      FacilityReview,
      | 'id'
      | 'tenantId'
      | 'status'
      | 'verifiedBooking'
      | 'helpfulVotes'
      | 'totalVotes'
      | 'createdAt'
      | 'updatedAt'
    >,
  ): Promise<FacilityReview> {
    const fullReviewData = {
      tenantId,
      status: 'pending' as const,
      verifiedBooking: false,
      helpfulVotes: 0,
      totalVotes: 0,
      ...reviewData,
    };
    return apiClient.post<FacilityReview>('/facilities/reviews', fullReviewData);
  }

  async updateReview(reviewId: string, updates: Partial<FacilityReview>): Promise<FacilityReview> {
    return apiClient.put<FacilityReview>(`/facilities/reviews/${reviewId}`, updates);
  }

  async deleteReview(reviewId: string): Promise<void> {
    await apiClient.delete(`/facilities/reviews/${reviewId}`);
  }

  // Maintenance
  async getMaintenanceSchedules(facilityId?: string): Promise<MaintenanceSchedule[]> {
    return apiClient.get<MaintenanceSchedule[]>('/facilities/maintenance', {
      facilityId,
    });
  }

  async createMaintenanceSchedule(
    scheduleData: Omit<MaintenanceSchedule, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<MaintenanceSchedule> {
    return apiClient.post<MaintenanceSchedule>('/facilities/maintenance', scheduleData);
  }

  async updateMaintenanceSchedule(
    scheduleId: string,
    updates: Partial<MaintenanceSchedule>,
  ): Promise<MaintenanceSchedule> {
    return apiClient.put<MaintenanceSchedule>(`/facilities/maintenance/${scheduleId}`, updates);
  }

  async deleteMaintenanceSchedule(scheduleId: string): Promise<void> {
    await apiClient.delete(`/facilities/maintenance/${scheduleId}`);
  }

  // Check-in/Check-out
  async checkIn(bookingId: string, userId: string): Promise<FacilityBooking> {
    return apiClient.put<FacilityBooking>(`/facilities/bookings/${bookingId}/checkin`, {
      checkedInBy: userId,
    });
  }

  async checkOut(bookingId: string, userId: string): Promise<FacilityBooking> {
    return apiClient.put<FacilityBooking>(`/facilities/bookings/${bookingId}/checkout`, {
      checkedOutBy: userId,
    });
  }

  // Reviews (additional methods)
  async getReviews(tenantId: string, facilityId?: string): Promise<FacilityReview[]> {
    return apiClient.get<FacilityReview[]>('/facilities/reviews', {
      tenantId,
      facilityId,
    });
  }
}

export const facilityService = new FacilityService();
